import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Trophy, 
  Plus, 
  Calendar, 
  MapPin, 
  User, 
  Medal, 
  Clock,
  Edit,
  Trash2,
  X,
  ChevronDown,
  ChevronUp,
  Award,
  Target,
  Filter,
  Swords,
  Dumbbell
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// BJJ Competition result types
const BJJ_RESULT_TYPES = [
  { value: 'win_submission', label: 'Win by Submission', color: 'text-green-500', bg: 'bg-green-500/20' },
  { value: 'win_points', label: 'Win by Points', color: 'text-green-400', bg: 'bg-green-500/10' },
  { value: 'win_advantage', label: 'Win by Advantage', color: 'text-green-400', bg: 'bg-green-500/10' },
  { value: 'win_referee', label: 'Win by Referee Decision', color: 'text-green-400', bg: 'bg-green-500/10' },
  { value: 'win_dq', label: 'Win by DQ', color: 'text-green-300', bg: 'bg-green-500/10' },
  { value: 'loss_submission', label: 'Loss by Submission', color: 'text-red-500', bg: 'bg-red-500/20' },
  { value: 'loss_points', label: 'Loss by Points', color: 'text-red-400', bg: 'bg-red-500/10' },
  { value: 'loss_advantage', label: 'Loss by Advantage', color: 'text-red-400', bg: 'bg-red-500/10' },
  { value: 'loss_referee', label: 'Loss by Referee Decision', color: 'text-red-400', bg: 'bg-red-500/10' },
  { value: 'loss_dq', label: 'Loss by DQ', color: 'text-red-300', bg: 'bg-red-500/10' },
  { value: 'draw', label: 'Draw', color: 'text-zinc-400', bg: 'bg-zinc-500/10' },
];

// MMA Competition result types
const MMA_RESULT_TYPES = [
  { value: 'win_ko', label: 'Win by KO/TKO', color: 'text-green-500', bg: 'bg-green-500/20' },
  { value: 'win_submission', label: 'Win by Submission', color: 'text-green-500', bg: 'bg-green-500/20' },
  { value: 'win_decision_unanimous', label: 'Win by Unanimous Decision', color: 'text-green-400', bg: 'bg-green-500/10' },
  { value: 'win_decision_split', label: 'Win by Split Decision', color: 'text-green-400', bg: 'bg-green-500/10' },
  { value: 'win_decision_majority', label: 'Win by Majority Decision', color: 'text-green-400', bg: 'bg-green-500/10' },
  { value: 'win_dq', label: 'Win by DQ', color: 'text-green-300', bg: 'bg-green-500/10' },
  { value: 'win_doctor_stoppage', label: 'Win by Doctor Stoppage', color: 'text-green-300', bg: 'bg-green-500/10' },
  { value: 'loss_ko', label: 'Loss by KO/TKO', color: 'text-red-500', bg: 'bg-red-500/20' },
  { value: 'loss_submission', label: 'Loss by Submission', color: 'text-red-500', bg: 'bg-red-500/20' },
  { value: 'loss_decision_unanimous', label: 'Loss by Unanimous Decision', color: 'text-red-400', bg: 'bg-red-500/10' },
  { value: 'loss_decision_split', label: 'Loss by Split Decision', color: 'text-red-400', bg: 'bg-red-500/10' },
  { value: 'loss_decision_majority', label: 'Loss by Majority Decision', color: 'text-red-400', bg: 'bg-red-500/10' },
  { value: 'loss_dq', label: 'Loss by DQ', color: 'text-red-300', bg: 'bg-red-500/10' },
  { value: 'loss_doctor_stoppage', label: 'Loss by Doctor Stoppage', color: 'text-red-300', bg: 'bg-red-500/10' },
  { value: 'draw', label: 'Draw', color: 'text-zinc-400', bg: 'bg-zinc-500/10' },
  { value: 'no_contest', label: 'No Contest', color: 'text-zinc-400', bg: 'bg-zinc-500/10' },
];

const MEDAL_TYPES = [
  { value: 'gold', label: 'Gold', color: 'text-yellow-400', icon: '🥇' },
  { value: 'silver', label: 'Silver', color: 'text-gray-300', icon: '🥈' },
  { value: 'bronze', label: 'Bronze', color: 'text-amber-600', icon: '🥉' },
  { value: 'none', label: 'No Medal', color: 'text-zinc-500', icon: '' },
];

const WEIGHT_CLASSES_BJJ = [
  'Rooster', 'Light Feather', 'Feather', 'Light', 'Middle', 
  'Medium Heavy', 'Heavy', 'Super Heavy', 'Ultra Heavy', 'Absolute'
];

const WEIGHT_CLASSES_MMA = [
  'Strawweight', 'Flyweight', 'Bantamweight', 'Featherweight', 
  'Lightweight', 'Welterweight', 'Middleweight', 'Light Heavyweight', 
  'Heavyweight', 'Super Heavyweight', 'Catchweight'
];

const SUBMISSION_TYPES = [
  'Armbar', 'Triangle', 'Rear Naked Choke', 'Guillotine', 'Kimura', 'Americana',
  'Bow and Arrow', 'Collar Choke', 'Ezekiel', 'Heel Hook', 'Knee Bar', 'Toe Hold',
  'Wrist Lock', 'D\'Arce', 'Anaconda', 'Peruvian Necktie', 'North-South Choke',
  'Arm Triangle', 'Omoplata', 'Gogoplata', 'Other'
];

const MMA_FINISH_TYPES = [
  'Punches', 'Elbows', 'Kicks', 'Knees', 'Head Kick', 'Body Shot',
  'Ground and Pound', 'Slam', 'Corner Stoppage', 'Other'
];

// Single Competition Record Card
const CompetitionCard = ({ record, onEdit, onDelete, isCoach }) => {
  const [expanded, setExpanded] = useState(false);
  const isMMA = record.competition_type === 'mma';
  const resultTypes = isMMA ? MMA_RESULT_TYPES : BJJ_RESULT_TYPES;
  const resultInfo = resultTypes.find(r => r.value === record.result) || resultTypes[0];
  const medalInfo = MEDAL_TYPES.find(m => m.value === record.medal_placement) || MEDAL_TYPES[3];
  const isWin = record.result?.startsWith('win');
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-900/50 border border-zinc-800 rounded-sm overflow-hidden"
      data-testid={`competition-record-${record.id}`}
    >
      {/* Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-zinc-800/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            {/* Result indicator */}
            <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${resultInfo.bg}`}>
              {isWin ? (
                isMMA ? <Swords className={`w-6 h-6 ${resultInfo.color}`} /> : <Trophy className={`w-6 h-6 ${resultInfo.color}`} />
              ) : (
                <Target className="w-6 h-6 text-red-500" />
              )}
            </div>
            
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${isMMA ? 'bg-red-600/20 text-red-400' : 'bg-blue-600/20 text-blue-400'}`}>
                  {isMMA ? 'MMA' : 'BJJ'}
                </span>
                <h4 className="text-white font-medium">{record.tournament_name}</h4>
                {medalInfo.icon && (
                  <span className="text-lg">{medalInfo.icon}</span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1 text-sm text-zinc-400">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {new Date(record.event_date).toLocaleDateString('en-US', { 
                    month: 'short', day: 'numeric', year: 'numeric' 
                  })}
                </span>
                {record.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {record.location}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <span className={`text-sm font-medium ${resultInfo.color}`}>
              {resultInfo.label}
            </span>
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-zinc-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-zinc-500" />
            )}
          </div>
        </div>
      </div>
      
      {/* Expanded Details */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-zinc-800"
          >
            <div className="p-4 space-y-4">
              {/* Match Details Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {isMMA ? (
                  <>
                    <div>
                      <p className="text-xs text-zinc-500 mb-1">Weight Class</p>
                      <p className="text-zinc-300">{record.weight_class || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 mb-1">Round</p>
                      <p className="text-zinc-300">{record.finish_round ? `Round ${record.finish_round}` : 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 mb-1">Time</p>
                      <p className="text-zinc-300">{record.finish_time || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 mb-1">Fight Type</p>
                      <p className="text-zinc-300 capitalize">{record.fight_type || 'N/A'}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <p className="text-xs text-zinc-500 mb-1">Division</p>
                      <p className="text-zinc-300">{record.division || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 mb-1">Weight Class</p>
                      <p className="text-zinc-300">{record.weight_class || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 mb-1">Gi / No-Gi</p>
                      <p className="text-zinc-300 capitalize">{record.gi_nogi || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 mb-1">Round</p>
                      <p className="text-zinc-300 capitalize">{record.round || 'N/A'}</p>
                    </div>
                  </>
                )}
              </div>
              
              {/* Opponent Info */}
              {record.opponent_name && (
                <div className="bg-zinc-800/50 rounded-sm p-3">
                  <p className="text-xs text-zinc-500 mb-2">Opponent</p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-zinc-700 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-zinc-400" />
                    </div>
                    <div>
                      <p className="text-white">{record.opponent_name}</p>
                      {record.opponent_team && (
                        <p className="text-zinc-500 text-sm">{record.opponent_team}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Finish Details */}
              {(record.result?.includes('submission') || record.result?.includes('ko')) && (
                <div className="bg-zinc-800/50 rounded-sm p-3">
                  <p className="text-xs text-zinc-500 mb-1">Finish Details</p>
                  <p className="text-white font-medium">
                    {record.submission_type || record.finish_type || 'N/A'}
                  </p>
                  {(record.submission_round || record.finish_round) && (
                    <p className="text-zinc-400 text-sm mt-1">
                      <Clock className="w-3.5 h-3.5 inline mr-1" />
                      Round {record.submission_round || record.finish_round} 
                      {(record.submission_time || record.finish_time) && ` at ${record.submission_time || record.finish_time}`}
                    </p>
                  )}
                </div>
              )}
              
              {/* Notes */}
              {record.notes && (
                <div>
                  <p className="text-xs text-zinc-500 mb-1">Notes</p>
                  <p className="text-zinc-400 text-sm">{record.notes}</p>
                </div>
              )}
              
              {/* Actions */}
              {isCoach && (
                <div className="flex gap-2 pt-2 border-t border-zinc-800">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); onEdit(record); }}
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                    data-testid={`edit-competition-${record.id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); onDelete(record); }}
                    className="border-red-800 text-red-500 hover:bg-red-950"
                    data-testid={`delete-competition-${record.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Competition Form Dialog
const CompetitionFormDialog = ({ open, onOpenChange, record, fighterId, onSave }) => {
  const isEdit = Boolean(record?.id);
  const [saving, setSaving] = useState(false);
  const [competitionType, setCompetitionType] = useState('bjj'); // 'bjj' or 'mma'
  
  const [formData, setFormData] = useState({
    tournament_name: '',
    event_date: '',
    location: '',
    competition_type: 'bjj',
    // BJJ specific fields
    division: '',
    weight_class: '',
    gi_nogi: 'gi',
    round: '',
    result: 'win_points',
    medal_placement: 'none',
    opponent_name: '',
    opponent_team: '',
    submission_type: '',
    submission_round: '',
    submission_time: '',
    // MMA specific fields
    fight_type: 'professional',
    finish_type: '',
    finish_round: '',
    finish_time: '',
    notes: ''
  });
  
  useEffect(() => {
    if (record) {
      const recType = record.competition_type || 'bjj';
      setCompetitionType(recType);
      setFormData({
        tournament_name: record.tournament_name || '',
        event_date: record.event_date || '',
        location: record.location || '',
        competition_type: recType,
        division: record.division || '',
        weight_class: record.weight_class || '',
        gi_nogi: record.gi_nogi || 'gi',
        round: record.round || '',
        result: record.result || (recType === 'mma' ? 'win_decision_unanimous' : 'win_points'),
        medal_placement: record.medal_placement || 'none',
        opponent_name: record.opponent_name || '',
        opponent_team: record.opponent_team || '',
        submission_type: record.submission_type || '',
        submission_round: record.submission_round || '',
        submission_time: record.submission_time || '',
        fight_type: record.fight_type || 'professional',
        finish_type: record.finish_type || '',
        finish_round: record.finish_round || '',
        finish_time: record.finish_time || '',
        notes: record.notes || ''
      });
    } else {
      // Reset form for new record
      setFormData({
        tournament_name: '',
        event_date: '',
        location: '',
        competition_type: competitionType,
        division: '',
        weight_class: '',
        gi_nogi: 'gi',
        round: '',
        result: competitionType === 'mma' ? 'win_decision_unanimous' : 'win_points',
        medal_placement: 'none',
        opponent_name: '',
        opponent_team: '',
        submission_type: '',
        submission_round: '',
        submission_time: '',
        fight_type: 'professional',
        finish_type: '',
        finish_round: '',
        finish_time: '',
        notes: ''
      });
    }
  }, [record, open, competitionType]);
  
  const handleTypeChange = (type) => {
    setCompetitionType(type);
    setFormData(prev => ({
      ...prev,
      competition_type: type,
      result: type === 'mma' ? 'win_decision_unanimous' : 'win_points',
      weight_class: '' // Reset weight class when switching types
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const payload = {
        ...formData,
        competition_type: competitionType,
        fighter_id: fighterId
      };
      
      if (isEdit) {
        await axios.put(`${API}/api/competitions/${record.id}`, payload);
        toast.success('Competition record updated');
      } else {
        await axios.post(`${API}/api/competitions`, payload);
        toast.success('Competition record added');
      }
      
      onSave();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save competition record');
    } finally {
      setSaving(false);
    }
  };
  
  const isMMA = competitionType === 'mma';
  const resultTypes = isMMA ? MMA_RESULT_TYPES : BJJ_RESULT_TYPES;
  const weightClasses = isMMA ? WEIGHT_CLASSES_MMA : WEIGHT_CLASSES_BJJ;
  const showSubmissionFields = formData.result?.includes('submission');
  const showKOFields = formData.result?.includes('ko');
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-white uppercase tracking-tight flex items-center gap-2">
            {isMMA ? <Swords className="w-5 h-5 text-red-500" /> : <Trophy className="w-5 h-5 text-yellow-500" />}
            {isEdit ? 'Edit Competition' : 'Add Competition'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Competition Type Toggle */}
          {!isEdit && (
            <div className="flex gap-2 p-1 bg-zinc-800 rounded-sm">
              <button
                type="button"
                onClick={() => handleTypeChange('bjj')}
                className={`flex-1 py-2 px-4 rounded-sm font-heading text-sm uppercase tracking-wider transition-colors flex items-center justify-center gap-2 ${
                  !isMMA ? 'bg-blue-600 text-white' : 'text-zinc-400 hover:text-white'
                }`}
                data-testid="competition-type-bjj"
              >
                <Dumbbell className="w-4 h-4" />
                BJJ Tournament
              </button>
              <button
                type="button"
                onClick={() => handleTypeChange('mma')}
                className={`flex-1 py-2 px-4 rounded-sm font-heading text-sm uppercase tracking-wider transition-colors flex items-center justify-center gap-2 ${
                  isMMA ? 'bg-red-600 text-white' : 'text-zinc-400 hover:text-white'
                }`}
                data-testid="competition-type-mma"
              >
                <Swords className="w-4 h-4" />
                MMA Fight
              </button>
            </div>
          )}
          
          {/* Event Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">{isMMA ? 'Event Name *' : 'Tournament Name *'}</Label>
              <Input
                value={formData.tournament_name}
                onChange={(e) => setFormData({ ...formData, tournament_name: e.target.value })}
                placeholder={isMMA ? 'UFC 300, Bellator 290...' : 'IBJJF World Championship'}
                className="bg-zinc-800 border-zinc-700"
                required
                data-testid="competition-tournament-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">{isMMA ? 'Fight Date *' : 'Event Date *'}</Label>
              <Input
                type="date"
                value={formData.event_date}
                onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                required
                data-testid="competition-date-input"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Location</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Las Vegas, NV"
                className="bg-zinc-800 border-zinc-700"
                data-testid="competition-location-input"
              />
            </div>
            {isMMA ? (
              <div className="space-y-2">
                <Label className="text-zinc-300">Fight Type</Label>
                <Select 
                  value={formData.fight_type} 
                  onValueChange={(value) => setFormData({ ...formData, fight_type: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="competition-fight-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="amateur">Amateur</SelectItem>
                    <SelectItem value="exhibition">Exhibition</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <Label className="text-zinc-300">Division / Age Group</Label>
                <Input
                  value={formData.division}
                  onChange={(e) => setFormData({ ...formData, division: e.target.value })}
                  placeholder="Adult, Master 1, Juvenile..."
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="competition-division-input"
                />
              </div>
            )}
          </div>
          
          {/* Competition Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Weight Class</Label>
              <Select 
                value={formData.weight_class} 
                onValueChange={(value) => setFormData({ ...formData, weight_class: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="competition-weight-select">
                  <SelectValue placeholder="Select weight" />
                </SelectTrigger>
                <SelectContent>
                  {weightClasses.map(wc => (
                    <SelectItem key={wc} value={wc}>{wc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isMMA ? (
              <>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Scheduled Rounds</Label>
                  <Select 
                    value={formData.round} 
                    onValueChange={(value) => setFormData({ ...formData, round: value })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="competition-rounds-select">
                      <SelectValue placeholder="Select rounds" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 Rounds</SelectItem>
                      <SelectItem value="5">5 Rounds (Main Event)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Gi / No-Gi</Label>
                  <Select 
                    value={formData.gi_nogi} 
                    onValueChange={(value) => setFormData({ ...formData, gi_nogi: value })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="competition-gi-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gi">Gi</SelectItem>
                      <SelectItem value="nogi">No-Gi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Tournament Round</Label>
                  <Select 
                    value={formData.round} 
                    onValueChange={(value) => setFormData({ ...formData, round: value })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="competition-round-select">
                      <SelectValue placeholder="Select round" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="first">First Round</SelectItem>
                      <SelectItem value="quarterfinal">Quarterfinal</SelectItem>
                      <SelectItem value="semifinal">Semifinal</SelectItem>
                      <SelectItem value="final">Final</SelectItem>
                      <SelectItem value="bronze_match">Bronze Match</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>
          
          {/* Result & Placement */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">{isMMA ? 'Fight Result *' : 'Match Result *'}</Label>
              <Select 
                value={formData.result} 
                onValueChange={(value) => setFormData({ ...formData, result: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="competition-result-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {resultTypes.map(r => (
                    <SelectItem key={r.value} value={r.value}>
                      <span className={r.color}>{r.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!isMMA && (
              <div className="space-y-2">
                <Label className="text-zinc-300">Medal / Placement</Label>
                <Select 
                  value={formData.medal_placement} 
                  onValueChange={(value) => setFormData({ ...formData, medal_placement: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="competition-medal-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MEDAL_TYPES.map(m => (
                      <SelectItem key={m.value} value={m.value}>
                        <span className={m.color}>{m.icon} {m.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          
          {/* Opponent Info */}
          <div className="border-t border-zinc-800 pt-4">
            <h4 className="text-sm font-medium text-zinc-400 mb-3 flex items-center gap-2">
              <User className="w-4 h-4" />
              Opponent Details
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Opponent Name</Label>
                <Input
                  value={formData.opponent_name}
                  onChange={(e) => setFormData({ ...formData, opponent_name: e.target.value })}
                  placeholder="John Smith"
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="competition-opponent-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">{isMMA ? 'Opponent Team/Gym' : 'Opponent Team/Academy'}</Label>
                <Input
                  value={formData.opponent_team}
                  onChange={(e) => setFormData({ ...formData, opponent_team: e.target.value })}
                  placeholder={isMMA ? 'American Top Team' : 'Gracie Barra'}
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="competition-opponent-team-input"
                />
              </div>
            </div>
          </div>
          
          {/* Finish Details (conditional for submissions and KOs) */}
          {(showSubmissionFields || showKOFields) && (
            <div className="border-t border-zinc-800 pt-4">
              <h4 className="text-sm font-medium text-zinc-400 mb-3 flex items-center gap-2">
                <Target className="w-4 h-4" />
                Finish Details
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">{showSubmissionFields ? 'Submission Type' : 'Finish Type'}</Label>
                  <Select 
                    value={showSubmissionFields ? formData.submission_type : formData.finish_type} 
                    onValueChange={(value) => setFormData({ 
                      ...formData, 
                      [showSubmissionFields ? 'submission_type' : 'finish_type']: value 
                    })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="competition-finish-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {(showSubmissionFields ? SUBMISSION_TYPES : MMA_FINISH_TYPES).map(item => (
                        <SelectItem key={item} value={item}>{item}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Round</Label>
                  <Input
                    type="number"
                    min="1"
                    max="5"
                    value={isMMA ? formData.finish_round : formData.submission_round}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      [isMMA ? 'finish_round' : 'submission_round']: e.target.value 
                    })}
                    placeholder="1"
                    className="bg-zinc-800 border-zinc-700"
                    data-testid="competition-finish-round-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Time</Label>
                  <Input
                    value={isMMA ? formData.finish_time : formData.submission_time}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      [isMMA ? 'finish_time' : 'submission_time']: e.target.value 
                    })}
                    placeholder="3:45"
                    className="bg-zinc-800 border-zinc-700"
                    data-testid="competition-finish-time-input"
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-zinc-300">Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder={isMMA ? 'Fight notes, gameplan execution, areas to improve...' : 'Match notes, takeaways, things to improve...'}
              className="bg-zinc-800 border-zinc-700 min-h-[80px]"
              data-testid="competition-notes-input"
            />
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className={`flex-1 text-white ${isMMA ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
              data-testid="save-competition-btn"
            >
              {saving ? 'Saving...' : isEdit ? 'Update' : isMMA ? 'Add MMA Fight' : 'Add BJJ Competition'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Main Competition Records Component
export const CompetitionRecords = ({ fighterId, isCoach = false }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [filter, setFilter] = useState('all'); // all, wins, losses
  
  useEffect(() => {
    loadRecords();
  }, [fighterId]);
  
  const loadRecords = async () => {
    try {
      const response = await axios.get(`${API}/api/competitions?fighter_id=${fighterId}`);
      setRecords(response.data || []);
    } catch (error) {
      console.error('Failed to load competition records');
    } finally {
      setLoading(false);
    }
  };
  
  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormDialogOpen(true);
  };
  
  const handleDelete = (record) => {
    setRecordToDelete(record);
    setDeleteDialogOpen(true);
  };
  
  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/api/competitions/${recordToDelete.id}`);
      toast.success('Competition record deleted');
      loadRecords();
    } catch (error) {
      toast.error('Failed to delete record');
    } finally {
      setDeleteDialogOpen(false);
      setRecordToDelete(null);
    }
  };
  
  const handleAddNew = () => {
    setEditingRecord(null);
    setFormDialogOpen(true);
  };
  
  // Calculate stats
  const stats = {
    total: records.length,
    wins: records.filter(r => r.result?.startsWith('win')).length,
    losses: records.filter(r => r.result?.startsWith('loss')).length,
    draws: records.filter(r => r.result === 'draw').length,
    submissions: records.filter(r => r.result === 'win_submission').length,
    gold: records.filter(r => r.medal_placement === 'gold').length,
    silver: records.filter(r => r.medal_placement === 'silver').length,
    bronze: records.filter(r => r.medal_placement === 'bronze').length,
  };
  
  // Filter records
  const filteredRecords = records.filter(r => {
    if (filter === 'wins') return r.result?.startsWith('win');
    if (filter === 'losses') return r.result?.startsWith('loss');
    return true;
  });
  
  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
        <div className="flex items-center justify-center h-32">
          <div className="w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="competition-records-section">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-yellow-500/20 rounded-sm flex items-center justify-center">
            <Trophy className="w-5 h-5 text-yellow-500" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-bold text-white uppercase tracking-tight">
              Competition Record
            </h3>
            <p className="text-zinc-500 text-sm">{stats.total} competition{stats.total !== 1 ? 's' : ''} recorded</p>
          </div>
        </div>
        
        {isCoach && (
          <Button
            onClick={handleAddNew}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="add-competition-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Competition
          </Button>
        )}
      </div>
      
      {/* Stats Overview */}
      {records.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          <div className="bg-zinc-800/50 rounded-sm p-3 text-center">
            <p className="text-2xl font-heading text-white">{stats.wins}</p>
            <p className="text-xs text-green-500">Wins</p>
          </div>
          <div className="bg-zinc-800/50 rounded-sm p-3 text-center">
            <p className="text-2xl font-heading text-white">{stats.losses}</p>
            <p className="text-xs text-red-500">Losses</p>
          </div>
          <div className="bg-zinc-800/50 rounded-sm p-3 text-center">
            <p className="text-2xl font-heading text-white">{stats.submissions}</p>
            <p className="text-xs text-blue-400">Subs</p>
          </div>
          <div className="bg-zinc-800/50 rounded-sm p-3 text-center">
            <p className="text-2xl font-heading text-white">
              {stats.total > 0 ? Math.round((stats.wins / stats.total) * 100) : 0}%
            </p>
            <p className="text-xs text-zinc-400">Win Rate</p>
          </div>
          <div className="bg-zinc-800/50 rounded-sm p-3 text-center">
            <p className="text-2xl font-heading text-yellow-400">{stats.gold}</p>
            <p className="text-xs text-yellow-500">Gold</p>
          </div>
          <div className="bg-zinc-800/50 rounded-sm p-3 text-center">
            <p className="text-2xl font-heading text-gray-300">{stats.silver}</p>
            <p className="text-xs text-gray-400">Silver</p>
          </div>
          <div className="bg-zinc-800/50 rounded-sm p-3 text-center">
            <p className="text-2xl font-heading text-amber-600">{stats.bronze}</p>
            <p className="text-xs text-amber-500">Bronze</p>
          </div>
        </div>
      )}
      
      {/* Filter */}
      {records.length > 0 && (
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-zinc-500" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700" data-testid="competition-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="wins">Wins Only</SelectItem>
              <SelectItem value="losses">Losses Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      
      {/* Records List */}
      {filteredRecords.length > 0 ? (
        <div className="space-y-3">
          {filteredRecords.map(record => (
            <CompetitionCard
              key={record.id}
              record={record}
              isCoach={isCoach}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      ) : (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-8 text-center">
          <Trophy className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <h4 className="text-white font-medium mb-2">
            {filter !== 'all' ? 'No matches found' : 'No competitions recorded'}
          </h4>
          <p className="text-zinc-500 text-sm mb-4">
            {filter !== 'all' 
              ? 'Try adjusting the filter' 
              : 'Start tracking BJJ tournament results and competition history'
            }
          </p>
          {isCoach && filter === 'all' && (
            <Button
              onClick={handleAddNew}
              className="bg-red-600 hover:bg-red-700"
              data-testid="add-first-competition-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add First Competition
            </Button>
          )}
        </div>
      )}
      
      {/* Form Dialog */}
      <CompetitionFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        record={editingRecord}
        fighterId={fighterId}
        onSave={loadRecords}
      />
      
      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Competition Record</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to delete this competition record for {recordToDelete?.tournament_name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-competition-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CompetitionRecords;
